import React, { memo } from "react";
import useCreateEventForm from "./index.hooks";
import {
  FormControlLabel,
  Checkbox,
  Grid,
  Hidden,
  Tabs,
  Typography,
} from "@material-ui/core";
import {
  AppButton,
  AppDatePicker,
  AppPaper,
  AppStepper,
  AppTab,
  AppTextField,
  SmartphoneEventPreview,
  TabPanel,
} from "components";
import { FieldError } from "react-hook-form";
import ImageIcon from "@material-ui/icons/Image";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import SettingsIcon from "@material-ui/icons/Settings";
import UploadFile from "components/UploadFile";
import { Event } from "models";
import moment from "moment";
import cn from "classnames";

type CreateEventFormProps = {};

const CreateEventForm = ({}: CreateEventFormProps) => {
  const {
    styles,
    classes,
    atomicStyles,
    onSubmit,
    register,
    watch,
    control,
    errors,
    TabsEnum,
    currentTab,
    handleTabChange,
    imageB64,
    uploadLogo,
    maxTitleLength,
    maxContentLength,
    handleDialogClose,
    isLoading,
    changeCurrentTab,
    titles,
    t,
    singleUseChecked,
    handleSingleUseChange,
  } = useCreateEventForm();

  return (
    <Grid container className={styles.mainContainer}>
      <Grid item md={8} sm={12} xs={12}>
        <form onSubmit={onSubmit}>
          <Hidden xsDown>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              centered
              classes={classes.tabs}
            >
              <AppTab
                error={!!errors["imageUri"]}
                value={TabsEnum.IMAGE}
                label={t("Banner")}
                icon={<ImageIcon />}
                fullWidth
              />
              <AppTab
                error={!!errors["title"] || !!errors["content"]}
                value={TabsEnum.TEXTS}
                label={t("Testi")}
                icon={<ChatRoundedIcon />}
                fullWidth
              />
              <AppTab
                error={!!errors["validFrom"] || !!errors["expire"]}
                value={TabsEnum.GENERAL}
                label={t("Generali")}
                icon={<SettingsIcon />}
                fullWidth
              />
            </Tabs>
          </Hidden>
          <Hidden smUp>
            <div style={{ width: "100%", textAlign: "center" }}>
              <Typography color={"primary"} variant={"h6"}>
                {titles[currentTab]}
              </Typography>
            </div>
          </Hidden>
          <Grid container direction="column" justify="space-between">
            <Grid item>
              <AppPaper className={styles.tabContent}>
                <TabPanel value={currentTab} index={TabsEnum.IMAGE}>
                  <UploadFile
                    maxFileSize={1000000}
                    filesLimit={1}
                    label={t("Banner dell'evento")}
                    name="imageUri"
                    dropzoneText={t(
                      "Trascina qui un'immagine o clicca per caricarne una!",
                    )}
                    onChange={uploadLogo}
                    error={!!errors["imageUri"]}
                    helperText={errors["imageUri"]?.message}
                    inputRef={register}
                  />
                </TabPanel>
                <TabPanel value={currentTab} index={TabsEnum.TEXTS}>
                  <AppTextField
                    disabled={isLoading}
                    label={t("Titolo")}
                    name="title"
                    error={!!errors["title"]}
                    helperText={errors["title"]?.message}
                    fullWidth
                    variant="outlined"
                    inputRef={register}
                    maxChars={maxTitleLength}
                    currentCount={watch("title")?.length ?? 0}
                  />
                  <AppTextField
                    disabled={isLoading}
                    label={t("Contenuto")}
                    name="content"
                    error={!!errors["content"]}
                    helperText={errors["content"]?.message}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    inputRef={register}
                    maxChars={maxContentLength}
                    currentCount={watch("content")?.length ?? 0}
                  />
                </TabPanel>
                <TabPanel value={currentTab} index={TabsEnum.GENERAL}>
                  <AppDatePicker
                    disabled={isLoading}
                    control={control}
                    defaultValue={watch("validFrom") ?? null}
                    label={t("Data di inizio")}
                    name="validFrom"
                    error={!!errors["validFrom"]}
                    helperText={
                      ((errors["validFrom"] as unknown) as FieldError)?.message
                    }
                  />
                  <AppDatePicker
                    disabled={isLoading}
                    control={control}
                    minDate={watch("validFrom") ?? null}
                    defaultValue={watch("expire") ?? null}
                    name="expire"
                    label={t("Fino al")}
                    error={!!errors["expire"]}
                    helperText={
                      ((errors["expire"] as unknown) as FieldError)?.message
                    }
                  />
                  <FormControlLabel
                    className={styles.formControlLabel}
                    value={singleUseChecked}
                    control={
                      <Checkbox
                        color="primary"
                        name="isSingleUse"
                        checked={singleUseChecked}
                        onChange={handleSingleUseChange}
                      />
                    }
                    label="Singolo utilizzo"
                    labelPlacement="end"
                    inputRef={register}
                  />
                </TabPanel>
              </AppPaper>
            </Grid>
            <Hidden xsDown>
              <Grid container item alignItems="center">
                <AppButton
                  disabled={isLoading}
                  onClick={handleDialogClose}
                  color="secondary"
                >
                  {t("Annulla")}
                </AppButton>
                <AppButton
                  className={cn(
                    styles.submitEventButton,
                    Object.keys(errors).length !== 0
                      ? styles.submitEventErrorAnimation
                      : null,
                  )}
                  disabled={isLoading || Object.keys(errors).length !== 0}
                  variant="contained"
                  color="primary"
                  type="submit"
                >
                  {t("Crea Evento")}
                </AppButton>
              </Grid>
            </Hidden>
            <Hidden smUp>
              <AppStepper
                steps={3}
                activeStep={currentTab}
                position={"bottom"}
                stepChanger={changeCurrentTab}
                stepStartComp={
                  <AppButton
                    disabled={isLoading}
                    onClick={handleDialogClose}
                    color="secondary"
                    className={styles.undoButton}
                  >
                    {t("Annulla")}
                  </AppButton>
                }
                stepFinalComp={
                  <AppButton
                    className={styles.createButton}
                    disabled={isLoading || Object.keys(errors).length !== 0}
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                  >
                    {t("Crea")}
                  </AppButton>
                }
              />
            </Hidden>
          </Grid>
        </form>
      </Grid>
      <Grid
        container
        item
        md={4}
        sm={false}
        xs={false}
        justify="center"
        alignItems="center"
      >
        <SmartphoneEventPreview
          event={
            new Event({
              _id: "",
              title: watch("title") ?? "",
              content: watch("content") ?? "",
              imageUri: imageB64,
              validFrom:
                watch("validFrom")?.toISOString() ?? moment().toISOString(),
              expire:
                watch("expire")?.toISOString() ??
                moment().add(1, "days").toISOString(),
            })
          }
        />
      </Grid>
    </Grid>
  );
};

export default memo(CreateEventForm);
